/**
 * buildSynthesisConclusion — deterministic conclusion bodies for
 * custom-brief and executive-summary when the LLM omits structured fields.
 */
import type {
  CustomBriefAnswer,
  CustomBriefConclusion,
  CustomBriefExecutiveSummary,
  CustomBriefOverallVerdict,
  FindingClaimEntry,
  SiteRagReport,
} from '../types/siteRag';
import {
  findingClaimEntriesToStrings,
  formatFindingClaimDisplay,
  type FindingClaim,
} from './findingClaim';

type ReportConclusion = NonNullable<SiteRagReport['conclusion']>;
type RecommendationVerdict = ReportConclusion['recommendation'];

const CONFIDENCE_RANK: Record<CustomBriefAnswer['confidence'], number> = {
  high: 3,
  medium: 2,
  low: 1,
};

function verdictToRecommendation(verdict: CustomBriefOverallVerdict): RecommendationVerdict {
  if (verdict === 'PROCEED') return 'Proceed';
  if (verdict === 'STOP') return 'Do Not Proceed';
  return 'Proceed with Conditions';
}

function inferRecommendationFromText(text: string): RecommendationVerdict {
  const lower = text.toLowerCase();
  // P1.4 — a withheld / pending headline must not default to a directive.
  if (/decision pending|recommendation withheld|verdict withheld/.test(lower)) return 'Decision Pending';
  if (lower.includes('do not proceed')) return 'Do Not Proceed';
  if (lower.includes('proceed with conditions')) return 'Proceed with Conditions';
  if (/\bproceed\b/.test(lower)) return 'Proceed';
  return 'Proceed with Conditions';
}

function uniqueNonEmpty(items: FindingClaimEntry[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    // A narrative leaf the reviewer-voice gate withheld is null on the body.
    if (raw == null) continue;
    const s = formatFindingClaimDisplay(raw);
    if (!s || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function topRedFlagAnswer(answers: readonly CustomBriefAnswer[]): CustomBriefAnswer | undefined {
  const flagged = answers.filter(a =>
    a.redFlags.some(r => formatFindingClaimDisplay(r).length > 0),
  );
  if (flagged.length === 0) return undefined;
  return [...flagged].sort((a, b) => CONFIDENCE_RANK[b.confidence] - CONFIDENCE_RANK[a.confidence])[0];
}

export function isThinSynthesisConclusion(conclusion: ReportConclusion | undefined): boolean {
  if (!conclusion) return true;
  // A withheld (null) bullet is not content; it must not throw either.
  const written = (s: unknown): boolean => typeof s === 'string' && s.trim().length > 0;
  const hasBullets =
    (conclusion.topStrengths?.some(written) ?? false)
    || (conclusion.topRisks?.some(written) ?? false)
    || (conclusion.nextActions?.some(written) ?? false);
  const hasSummary = Boolean(conclusion.btmDecisionSummary?.trim());
  return !hasBullets && !hasSummary;
}

export interface CustomBriefSynthesisInput {
  structuredExec?: CustomBriefExecutiveSummary;
  customBriefAnswers: readonly CustomBriefAnswer[];
  btmDecisionSummary?: string;
  existing?: CustomBriefConclusion;
}

export interface CustomBriefSynthesisResult {
  customBriefConclusion: CustomBriefConclusion;
  conclusion: ReportConclusion;
  btmDecisionSummary?: string;
}

export function buildCustomBriefSynthesisConclusion(
  input: CustomBriefSynthesisInput,
): CustomBriefSynthesisResult {
  const { structuredExec, customBriefAnswers, btmDecisionSummary } = input;
  const existing = input.existing;

  const topStrengths = uniqueNonEmpty([
    ...(existing?.topStrengths ?? []),
    ...(structuredExec?.keyFindings.slice(0, 2) ?? []),
  ]);

  const flagged = topRedFlagAnswer(customBriefAnswers);
  const keyRiskStrings = uniqueNonEmpty([
    ...(existing?.keyRisks ?? []),
    ...(structuredExec?.materialRisks.slice(0, 2) ?? []),
    ...(flagged?.redFlags.slice(0, 2) ?? []),
  ]);
  const keyRisks: FindingClaim[] = keyRiskStrings.map((claim) => ({
    claim,
    evidenceState: 'unknown' as const,
    openQuestion: `Confirm: ${claim}`,
  }));

  const redFlagThemes = customBriefAnswers
    .flatMap(a => a.redFlags)
    .map(r => formatFindingClaimDisplay(r))
    .filter(Boolean)
    .slice(0, 3);

  const recommendedNextActions = existing?.recommendedNextActions?.length
    ? existing.recommendedNextActions
    : uniqueNonEmpty([
      structuredExec?.recommendedDecision ?? '',
      ...redFlagThemes.map(theme => `Pressure-test: ${theme}`),
    ]).slice(0, 3).map((action, i) => ({
      priority: (i === 0 ? 'immediate' : 'pre-FID') as CustomBriefConclusion['recommendedNextActions'][number]['priority'],
      action,
      rationale: structuredExec?.materialRisks[0]
        ? formatFindingClaimDisplay(structuredExec.materialRisks[0])
        : 'Derived from executive summary and diligence red flags.',
    }));

  const customBriefConclusion: CustomBriefConclusion = {
    topStrengths: topStrengths.slice(0, 5),
    keyRisks: keyRisks.slice(0, 5),
    recommendedNextActions,
  };

  const recommendation = structuredExec
    ? verdictToRecommendation(structuredExec.overallVerdict)
    : 'Proceed with Conditions';

  const summary = btmDecisionSummary?.trim()
    ?? structuredExec?.recommendedDecision?.trim();

  return {
    customBriefConclusion,
    ...(summary ? { btmDecisionSummary: summary } : {}),
    conclusion: {
      recommendation,
      overallScore: 0,
      topStrengths: customBriefConclusion.topStrengths,
      topRisks: findingClaimEntriesToStrings(customBriefConclusion.keyRisks),
      keyConditions: [],
      nextActions: customBriefConclusion.recommendedNextActions.map(a => a.action),
      ...(summary ? { btmDecisionSummary: summary } : {}),
    },
  };
}

export interface ExecutiveSummarySynthesisInput {
  btmViabilityAssessment?: string;
  executiveSummaryCriticalRisks?: Array<{ risk: string; severity?: string; mitigation?: string }>;
  actionPlan?: string[];
  keyFindings?: Array<{ title?: string; detail?: string; impact?: string }>;
  stringConclusion?: string;
  existing?: ReportConclusion;
  /**
   * P3-9 — firm-capacity (ELCC) shortfall signal, sourced from
   * `capTierByFirmCapacity` against the report's gen-time intent. The
   * exec-summary has no deterministic scorer (verdict tier 'none'), so the
   * headline GO/NO-GO was previously uncapped. When the declared mix cannot
   * firm the 24/7 load we reconcile the recommendation DOWN so the board-ready
   * call can never read an unconditional Proceed against a known firm-capacity
   * gap. `undefined`/'feasible' → no reconciliation (byte-stable).
   */
  firmCapacityWorstVerdict?: 'marginal' | 'infeasible' | null;
}

/**
 * P3-9 — reconcile the exec-summary recommendation with the firm-capacity cap.
 * The cap only ever LOWERS (never raises) the recommendation:
 *   infeasible → 'Do Not Proceed'        (the mix cannot serve the load)
 *   marginal   → at most 'Proceed with Conditions' (short of reserve margin)
 * A 'Do Not Proceed' is never softened by a 'marginal' cap.
 */
function reconcileRecommendationWithFirmCapacity(
  recommendation: RecommendationVerdict,
  worst: 'marginal' | 'infeasible' | null | undefined,
): RecommendationVerdict {
  if (worst === 'infeasible') return 'Do Not Proceed';
  if (worst === 'marginal' && recommendation === 'Proceed') {
    return 'Proceed with Conditions';
  }
  return recommendation;
}

export function buildExecutiveSummarySynthesisConclusion(
  input: ExecutiveSummarySynthesisInput,
): ReportConclusion {
  const existing = input.existing;
  const btmSummary = input.btmViabilityAssessment?.trim()
    ?? existing?.btmDecisionSummary?.trim();

  const positiveFindings = (input.keyFindings ?? [])
    .filter(f => f.impact?.toLowerCase() === 'positive' && f.title?.trim())
    .map(f => f.title!.trim());
  const topStrengths = uniqueNonEmpty([
    ...(existing?.topStrengths ?? []),
    ...positiveFindings.slice(0, 3),
  ]);

  const topRisks = uniqueNonEmpty([
    ...(existing?.topRisks ?? []),
    // A withheld (null) risk is skipped, never back-filled.
    ...(input.executiveSummaryCriticalRisks ?? [])
      .map(r => (typeof r?.risk === 'string' ? r.risk.trim() : ''))
      .filter(Boolean)
      .slice(0, 2),
  ]);

  const nextActions = uniqueNonEmpty([
    ...(existing?.nextActions ?? []),
    ...(input.actionPlan ?? []),
  ]).slice(0, 3);

  const baseRecommendation = existing?.recommendation
    ?? (input.stringConclusion?.trim()
      ? inferRecommendationFromText(input.stringConclusion)
      : 'Proceed with Conditions');
  // P3-9 — bind the headline GO/NO-GO to firm-capacity reality.
  const recommendation = reconcileRecommendationWithFirmCapacity(
    baseRecommendation,
    input.firmCapacityWorstVerdict,
  );

  return {
    recommendation,
    overallScore: existing?.overallScore ?? 0,
    topStrengths,
    topRisks,
    keyConditions: existing?.keyConditions ?? [],
    nextActions,
    ...(btmSummary ? { btmDecisionSummary: btmSummary } : {}),
    ...(input.stringConclusion?.trim() && !btmSummary
      ? { environmentalVerdict: input.stringConclusion.trim() }
      : {}),
  };
}
